<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { ContractsService } from '@/services/contractManagement/contractsService';
import { fetchWholeDirectory, itemsOfKind, vaultTypeLabel } from '@/services/vaultListingService';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import { logger } from '@/utils/logger';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaCheckbox from '../../base/GaiaCheckbox.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaSearchInput from '../../base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '../../base/selects/GaiaSelect.vue';
import GaiaDataTable, { type GaiaDataColumn } from '../../composite/GaiaDataTable.vue';
import GaiaEmptyState from '../../composite/GaiaEmptyState.vue';
import GaiaPagination from '../../composite/GaiaPagination.vue';
import GaiaTableSkeleton from '../../composite/GaiaTableSkeleton.vue';
import GaiaBreadcrumb from '../navigation/GaiaBreadcrumb.vue';
import { gaiaNeutral } from '../../themes/tokens';
import { IconDocument, IconFolder } from '../../icons';
import {
  emptyVaultPickerCriteria,
  type VaultPickKind,
  type VaultPickerCriteria,
  type VaultPickerScope,
} from './vaultPicker';

/**
 * The Contract Vault as a table, to pick folders and/or contracts from.
 *
 * `scope="vault"` browses folders and contracts; a search or type/tag filter
 * lists matching contracts instead (the vault search cannot filter by type or
 * tag, the contract search can). `scope="contracts"` always lists the contract
 * search results. Plain files hold no contract, so they are never listed.
 *
 * `selectable` names the kinds that get a checkbox — a folder stands for
 * everything in it, a contract for just that contract. With none, the table
 * only shows what the search matches. Bind `criteria` to keep the search;
 * unbound, it is the picker's own.
 */
const props = withDefaults(
  defineProps<{
    scope?: VaultPickerScope;
    selectable?: VaultPickKind[];
  }>(),
  {
    scope: 'vault',
    selectable: () => ['directory', 'contract'],
  }
);

const directoryUids = defineModel<string[]>('directoryUids', { default: () => [] });
const contractUids = defineModel<string[]>('contractUids', { default: () => [] });
const criteria = defineModel<VaultPickerCriteria>('criteria', {
  default: emptyVaultPickerCriteria,
});

const emit = defineEmits<{
  /** How many contracts the search matches right now. */
  count: [count: number];
}>();

interface Row {
  kind: VaultPickKind;
  uid: string;
  name: string;
  description: string | null;
  typeLabel: string;
  /** Contracts in a folder (recursive), files in a contract. */
  count: number | null;
  date: string | null;
}

interface Crumb {
  /** Null: the vault's root. */
  uid: string | null;
  label: string;
}

// The search field owns its debounce, so the text is held here and only
// becomes criteria once the search fires.
const query = ref<string>(criteria.value.text);
/** Every tag the contract search reported, for the tag filter's options. */
const contractTypeStore = useContractTypeStore();
const tagOptions = ref<string[]>([]);

const isPicking = computed(() => props.selectable.length > 0);
const hasFilters = computed(
  () =>
    criteria.value.text.length > 0 ||
    criteria.value.types.length > 0 ||
    criteria.value.tags.length > 0
);
/** Contract results rather than the folder view. */
const isSearching = computed(() => props.scope === 'contracts' || hasFilters.value);

const rows = ref<Row[]>([]);
const resultCount = ref<number>(0);
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);
const page = ref<number>(1);
const pageSize = ref<number>(10);
// Only the latest request may write rows, so a slow load can't overwrite a
// page, folder or filter change that landed after it.
let requestId = 0;

// ── Browse ──────────────────────────────────────────────────────────────────

const crumbs = ref<Crumb[]>([{ uid: null, label: 'My Vault' }]);
/** The open folder, whole: browsing pages it here. */
const folderRows = ref<Row[]>([]);

async function loadFolder(): Promise<void> {
  const current = ++requestId;
  loading.value = true;
  loadError.value = null;
  try {
    const directory = await fetchWholeDirectory(crumbs.value.at(-1)?.uid ?? undefined);
    if (current !== requestId) return;
    folderRows.value = [
      ...itemsOfKind(directory.items, 'directory').map((folder): Row => ({
        kind: 'directory',
        uid: folder.uid,
        name: folder.directory_name,
        description: null,
        typeLabel: vaultTypeLabel(folder.type_label),
        count: folder.file_count ?? null,
        date: folder.updated_at ?? folder.created_at,
      })),
      ...itemsOfKind(directory.items, 'contract').map((contract): Row => ({
        kind: 'contract',
        uid: contract.uid,
        name: contract.name,
        description: null,
        typeLabel: contract.contract_type?.name ?? '—',
        count: contract.contract_file_count,
        date: contract.updated_at ?? contract.created_at,
      })),
    ];
    showFolderPage();
  } catch (error) {
    if (current !== requestId) return;
    logger.error('Error loading vault folder for picker:', error);
    loadError.value = error instanceof Error ? error.message : 'Failed to load the folder';
  } finally {
    if (current === requestId) loading.value = false;
  }
}

function showFolderPage(): void {
  const start = (page.value - 1) * pageSize.value;
  rows.value = folderRows.value.slice(start, start + pageSize.value);
  resultCount.value = folderRows.value.length;
}

function openFolder(row: Row): void {
  crumbs.value = [...crumbs.value, { uid: row.uid, label: row.name }];
  page.value = 1;
  void loadFolder();
}

function openCrumb(index: number): void {
  if (index === crumbs.value.length - 1) return;
  crumbs.value = crumbs.value.slice(0, index + 1);
  page.value = 1;
  void loadFolder();
}

// ── Search ──────────────────────────────────────────────────────────────────

async function searchContracts(): Promise<void> {
  const current = ++requestId;
  const { text, types, tags } = criteria.value;
  loading.value = true;
  loadError.value = null;
  try {
    const result = await ContractsService.searchContracts({
      text,
      page: page.value,
      pageSize: pageSize.value,
      sortKey: 'name',
      sortDirection: 'asc',
      columnFilters: {
        ...(types.length > 0 ? { type: types } : {}),
        ...(tags.length > 0 ? { tags } : {}),
      },
    });
    if (current !== requestId) return;
    rows.value = result.results.map(contract => ({
      kind: 'contract',
      uid: contract.uid,
      name: contract.name,
      description: contract.description,
      typeLabel: contract.contract_type?.name ?? '—',
      count: contract.contract_file_count,
      date: contract.updated_at ?? contract.created_at,
    }));
    resultCount.value = result.result_count;
    emit('count', result.result_count);
  } catch (error) {
    if (current !== requestId) return;
    logger.error('Error searching contracts for picker:', error);
    loadError.value = error instanceof Error ? error.message : 'Failed to load contracts';
  } finally {
    if (current === requestId) loading.value = false;
  }
}

/**
 * Every tag, loaded once with no filter. A search's own facets would narrow to
 * the tags already picked, leaving no way to add another.
 */
async function loadTagOptions(): Promise<void> {
  try {
    const result = await ContractsService.searchContracts({ pageSize: 1 });
    tagOptions.value = result.filter_options.tags.filter(
      (tag): tag is string => typeof tag === 'string'
    );
  } catch (error) {
    logger.error('Error loading contract tags for picker:', error);
  }
}

// ── Loading ─────────────────────────────────────────────────────────────────

function load(): void {
  void (isSearching.value ? searchContracts() : loadFolder());
}

function applySearch(): void {
  patchCriteria({ text: query.value.trim() });
}

function patchCriteria(changes: Partial<VaultPickerCriteria>): void {
  criteria.value = { ...criteria.value, ...changes };
}

function clearFilters(): void {
  query.value = '';
  patchCriteria({ text: '', types: [], tags: [] });
}

// Any change to the criteria can leave the current page past the end, so
// start over.
watch(criteria, () => {
  page.value = 1;
  load();
});

function onPage(value: number): void {
  page.value = value;
  if (isSearching.value) void searchContracts();
  else showFolderPage();
}

function onPageSize(value: number): void {
  pageSize.value = value;
  onPage(1);
}

onMounted(() => {
  load();
  void contractTypeStore.ensureLoaded();
  void loadTagOptions();
});

// ── Selection ───────────────────────────────────────────────────────────────

const pickModels = { directory: directoryUids, contract: contractUids };

function isSelectable(row: Row): boolean {
  return props.selectable.includes(row.kind);
}

function isPicked(row: Row): boolean {
  return pickModels[row.kind].value.includes(row.uid);
}

function toggle(row: Row): void {
  if (!isSelectable(row)) return;
  const model = pickModels[row.kind];
  model.value = isPicked(row)
    ? model.value.filter(uid => uid !== row.uid)
    : [...model.value, row.uid];
}

// ── View ────────────────────────────────────────────────────────────────────

const tableColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'Name', width: 'minmax(240px,1.6fr)' },
  { key: 'type', label: 'Type', width: 'minmax(140px,0.8fr)' },
  { key: 'count', label: 'Files', width: '80px' },
  { key: 'date', label: 'Modified', width: 'minmax(120px,0.7fr)' },
];

const skeletonCells = ['70%', '55%', '30%', '60%'];

const showRows = computed(() => !loading.value && !loadError.value && rows.value.length > 0);
const showEmpty = computed(() => !loading.value && !loadError.value && rows.value.length === 0);

const truncateStyle = {
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
const rowPicked = gaiaNeutral.surfaceTint;
const iconColor = gaiaNeutral.textSubtle;
</script>

<template>
  <n-flex vertical :size="12" :wrap="false">
    <n-flex align="center" :size="10" :wrap="false">
      <gaia-search-input
        v-model:value="query"
        placeholder="Search contracts"
        :debounce="500"
        :style="{ flex: '1 1 auto', minWidth: 0 }"
        @search="applySearch"
      />
      <gaia-select
        :value="criteria.types"
        multiple
        clearable
        max-tag-count="responsive"
        placeholder="All types"
        filterable
        :options="contractTypeStore.nameOptions"
        :style="{ flex: 'none', width: '180px' }"
        @update:value="(value: string[]) => patchCriteria({ types: value })"
      />
      <gaia-select
        :value="criteria.tags"
        multiple
        clearable
        filterable
        max-tag-count="responsive"
        placeholder="All tags"
        :options="tagOptions.map(tag => ({ label: tag, value: tag }))"
        :style="{ flex: 'none', width: '180px' }"
        @update:value="(value: string[]) => patchCriteria({ tags: value })"
      />
    </n-flex>

    <gaia-breadcrumb v-if="!isSearching">
      <n-breadcrumb-item
        v-for="(crumb, index) in crumbs"
        :key="crumb.uid ?? 'root'"
        :clickable="index < crumbs.length - 1"
        @click="openCrumb(index)"
      >
        {{ crumb.label }}
      </n-breadcrumb-item>
    </gaia-breadcrumb>

    <gaia-data-table
      :columns="tableColumns"
      :lead-column="isPicking"
      :lead-width="40"
      :action-width="0"
    >
      <template #default="{ rowStyle }">
        <gaia-table-skeleton
          v-if="loading"
          :lead="isPicking"
          :action="false"
          :row-style="rowStyle"
          :row-height="52"
          :cells="skeletonCells"
          :rows="4"
        />

        <gaia-empty-state v-else-if="loadError" status="alert" :title="loadError">
          Nothing was changed — retrying is safe.
          <template #actions>
            <gaia-button type="primary" size="small" @click="load">Retry</gaia-button>
          </template>
        </gaia-empty-state>

        <gaia-empty-state
          v-else-if="showEmpty"
          dashed
          :title="
            !isSearching
              ? 'This folder holds no contracts'
              : hasFilters
                ? 'No contracts match'
                : 'No contracts you can read yet'
          "
        >
          {{
            !isSearching
              ? 'Open another folder, or search for contracts.'
              : hasFilters
                ? 'Try a different search or filter.'
                : 'Contracts appear here once they are in the vault.'
          }}
          <template v-if="hasFilters" #actions>
            <gaia-button size="small" @click="clearFilters">Clear filters</gaia-button>
          </template>
        </gaia-empty-state>

        <template v-else-if="showRows">
          <div
            v-for="row in rows"
            :key="`${row.kind}-${row.uid}`"
            class="row"
            :class="{ pickable: isSelectable(row), picked: isSelectable(row) && isPicked(row) }"
            :style="rowStyle"
            @click="toggle(row)"
          >
            <div v-if="isPicking" class="cell">
              <gaia-checkbox
                v-if="isSelectable(row)"
                :checked="isPicked(row)"
                :aria-label="`Select ${row.name}`"
                @click.stop
                @update:checked="toggle(row)"
              />
            </div>
            <n-flex align="center" :size="10" :wrap="false" :style="{ minWidth: 0 }">
              <n-icon
                :component="row.kind === 'directory' ? IconFolder : IconDocument"
                :size="16"
                :color="iconColor"
                :style="{ flex: 'none' }"
              />
              <n-flex vertical :size="2" :wrap="false" :style="{ minWidth: 0 }">
                <!-- A folder's name opens it; the rest of the row ticks it, if
                     folders can be picked. -->
                <span
                  v-if="row.kind === 'directory'"
                  class="open"
                  role="button"
                  tabindex="0"
                  :title="`Open ${row.name}`"
                  @click.stop="openFolder(row)"
                  @keydown.enter.stop="openFolder(row)"
                >
                  <gaia-text size="body" weight="semibold" tone="ink" :style="truncateStyle">
                    {{ row.name }}
                  </gaia-text>
                </span>
                <gaia-text
                  v-else
                  size="body"
                  weight="semibold"
                  tone="ink"
                  :title="row.name"
                  :style="truncateStyle"
                >
                  {{ row.name }}
                </gaia-text>
                <gaia-text
                  v-if="row.description"
                  size="dense"
                  tone="muted"
                  :title="row.description"
                  :style="truncateStyle"
                >
                  {{ row.description }}
                </gaia-text>
              </n-flex>
            </n-flex>
            <gaia-text size="dense">{{ row.typeLabel }}</gaia-text>
            <gaia-text size="dense" :style="{ fontVariantNumeric: 'tabular-nums' }">
              {{ row.count ?? '—' }}
            </gaia-text>
            <gaia-text size="dense" :style="{ fontVariantNumeric: 'tabular-nums' }">
              {{ row.date ? formatDateForDisplay(row.date) : '—' }}
            </gaia-text>
          </div>
        </template>
      </template>

      <template v-if="showRows" #footer>
        <gaia-pagination
          :page="page"
          :page-size="pageSize"
          :total="resultCount"
          @update:page="onPage"
          @update:page-size="onPageSize"
        />
      </template>
    </gaia-data-table>
  </n-flex>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  /* At least the skeleton's height; a description under the name grows it. */
  min-height: 52px;
  padding: 8px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
}

.row.pickable {
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.row.picked {
  background: v-bind(rowPicked);
}

.cell {
  display: flex;
  align-items: center;
  min-width: 0;
}

.open {
  min-width: 0;
  cursor: pointer;
}

.open:hover {
  text-decoration: underline;
}
</style>
